import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import { ChangeEvent, FormEvent, useRef, useState } from 'react';
import { IState, IUseSearchProps } from 'pages/Search/types';
import { useRouter } from 'modules/next/hooks';
import { AppRoutes } from 'modules/shop/routes';
import { SortOptions, SortOrder } from 'commerce/factFinder/queryParameters';

export const useSearch = ({
  api: {
    getPreSearchProductsClient,
    getSearchSuggestedCategories,
    getSearchSuggestedProducts,
  },
  isTablet,
  maxLength = Infinity,
}: IUseSearchProps) => {
  const SEARCH_MIN_SYMBOLS = 2;
  const { push, replace } = useRouter();

  const [state, updateState] = useState<IState>({});

  const fetchLongTailPageUrl = async (query: string) => {
    if (query?.length >= SEARCH_MIN_SYMBOLS) {
      return await getSearchSuggestedProducts({
        query,
        sortBy: { field: SortOptions.relevancy, direction: SortOrder.desc },
      });
    }
    return null;
  };

  const suggest = useRef(
    debounce(async (query: string) => {
      if (isTablet && query?.length >= SEARCH_MIN_SYMBOLS) {
        return Promise.all([
          getPreSearchProductsClient(query),
          getSearchSuggestedCategories(query),
          getSearchSuggestedProducts({
            query,
            sortBy: { field: SortOptions.relevancy, direction: SortOrder.desc },
          }),
        ]).then(([products, categoryList, { keywords, correspondingUrl }]) => {
          updateState((s) => ({
            ...s,
            correspondingUrl,
            keywords: keywords?.slice(0, maxLength) || [],
            suggestions: {
              ...s.suggestions,
              hits: products.data.hits?.slice(0, maxLength) || [],
              categoryList:
                categoryList
                  .filter((item) => item.name.includes(query))
                  ?.slice(0, maxLength) || [],
            },
          }));
        });
      } else {
        updateState((s) => ({
          isChanged: s.isChanged,
          searchValue: s.searchValue,
        }));
      }
    }, 500),
  );

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (state.searchValue) {
      if (!isTablet) {
        /** For mobile, fetch the correspondingUrl on submit */
        const result = await fetchLongTailPageUrl(state.searchValue);
        if (result && result.correspondingUrl) {
          return push(result.correspondingUrl);
        }
      } else if (state.correspondingUrl) {
        /** For desktop, use the correspondingUrl from state */
        return push(state.correspondingUrl);
      }

      /** If no correspondingUrl, use the default search URL */
      const url = new URL(AppRoutes.SEARCH, window.location.origin);
      url.searchParams.append('fss', state.searchValue);
      replace(url.href);
    }
  };

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    updateState((s) => ({
      ...s,
      searchValue: event.target.value,
      isChanged: true /** Fix for disabling emptyTitle */,
    }));

    return suggest.current(event.target.value.toLowerCase());
  };

  const clear = () => {
    updateState({});
  };

  const isEmptySuggestions = () => {
    if (
      (!state?.suggestions?.categoryList &&
        !state?.suggestions?.hits &&
        !state.keywords) ||
      isEmpty(state) ||
      state.searchValue?.length === 0
    ) {
      return false;
    }

    return (
      !state.keywords?.length &&
      !state?.suggestions?.categoryList?.length &&
      !state?.suggestions?.hits?.length
    );
  };

  return {
    state,
    clear,
    isEmptySuggestions: isEmptySuggestions(),
    handleSearchChange,
    handleSubmit,
    areSuggestionsShown: Boolean(
      (!!state.suggestions || !!state.keywords) && isTablet,
    ),
  };
};
